import { useEffect, useState } from "react";
import { RUN_DIFFICULTY_LEVELS, runDifficultySettings } from "@roguelife/engine";
import { useGame } from "./store";
import { NarrativeLog } from "./components/NarrativeLog";
import { ActionList } from "./components/ActionList";
import { CheckPanel } from "./components/CheckPanel";
import { SidePanel } from "./components/SidePanel";
import { SceneStage } from "./components/SceneStage";
import { Die } from "./components/Dice";
import { localArtUrl } from "./art";
import type { RunDifficultyLevel, RunMode, RunResultView } from "./types";

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function pct(n: number) {
  return `${Math.round(n * 100)}%`;
}

function scoreEcho(multiplier: number) {
  return `x${multiplier}`;
}

type FeatTone = "win" | "scar" | "echo" | "crown";

interface ChallengeFeat {
  label: string;
  short: string;
  tone: FeatTone;
}

function challengeFeats(result: RunResultView): ChallengeFeat[] {
  const items = new Set(result.itemIds);
  const events = new Set(result.eventIds);
  const feats: ChallengeFeat[] = [];
  if (items.has("19711")) feats.push({ label: "旧路灯记", short: "灯记", tone: "win" });
  if (items.has("19712")) feats.push({ label: "湿痕灯记", short: "湿痕", tone: "echo" });
  if (items.has("19721")) feats.push({ label: "裂命胜局", short: "胜局", tone: "win" });
  if (items.has("19722")) feats.push({ label: "裂光反噬", short: "反噬", tone: "scar" });
  if (items.has("19724")) feats.push({ label: "无归徽记", short: "徽记", tone: "win" });
  if (items.has("19725")) feats.push({ label: "裂钟冷痕", short: "冷痕", tone: "scar" });
  if (items.has("19727")) feats.push({ label: "逆命烛冠", short: "烛冠", tone: "win" });
  if (items.has("19728")) feats.push({ label: "烛灰债", short: "烛灰", tone: "scar" });
  if (items.has("19730")) feats.push({ label: "断界铭牌", short: "断界", tone: "crown" });
  if (items.has("19731")) feats.push({ label: "界裂余债", short: "界裂", tone: "scar" });
  if (events.has("20773")) feats.push({ label: "裂徽照夜", short: "照夜", tone: "echo" });
  if (events.has("20777")) feats.push({ label: "无归余响", short: "余响", tone: "echo" });
  if (events.has("20781")) feats.push({ label: "逆命回烛", short: "回烛", tone: "echo" });
  if (events.has("20785")) feats.push({ label: "断界回声", short: "界声", tone: "echo" });
  if (result.ending.id === "28015") feats.push({ label: "无归归来", short: "归来", tone: "crown" });
  if (result.ending.id === "28016") feats.push({ label: "断界归灯", short: "归灯", tone: "crown" });
  if (result.ending.id === "28017") feats.push({ label: "逆命归烛", short: "归烛", tone: "crown" });
  return feats;
}

const BADGE_TIERS: {
  level: RunDifficultyLevel;
  title: string;
  motto: string;
  glyph: string;
  unlock: string;
  prize: string;
  artRef?: string;
  earned: (result: RunResultView) => boolean;
  scarred?: (result: RunResultView) => boolean;
}[] = [
  {
    level: 6,
    title: "天命通关",
    motto: "压迫最强，仍留生路",
    glyph: "◆",
    unlock: "完成 D6 天命旅程",
    prize: "声望倍率封印",
    earned: () => true,
  },
  {
    level: 7,
    title: "裂隙通关",
    motto: "裂命赌局，夺回主动",
    glyph: "◇",
    artRef: "item:19721",
    unlock: "踏入裂命赌局",
    prize: "裂命胜局 / 裂光反噬",
    earned: (result) => result.eventIds.includes("20771") || result.itemIds.includes("19721"),
    scarred: (result) => result.eventIds.includes("20772") || result.itemIds.includes("19722"),
  },
  {
    level: 8,
    title: "无归铭刻",
    motto: "无归钟声，刻入首页",
    glyph: "◈",
    artRef: "item:19724",
    unlock: "敲响无归钟试炼",
    prize: "无归徽记 / 裂钟冷痕",
    earned: (result) => result.ending.id === "28015" || result.eventIds.includes("20775") || result.itemIds.includes("19724"),
    scarred: (result) => result.eventIds.includes("20776") || result.itemIds.includes("19725"),
  },
  {
    level: 9,
    title: "逆命烛冠",
    motto: "无归之后，点烛改命",
    glyph: "✦",
    artRef: "item:19727",
    unlock: "完成逆命烛试炼",
    prize: "逆命烛冠 / 烛灰债",
    earned: (result) => result.ending.id === "28017" || result.eventIds.includes("20779") || result.itemIds.includes("19727"),
    scarred: (result) => result.eventIds.includes("20780") || result.itemIds.includes("19728"),
  },
  {
    level: 10,
    title: "断界铭刻",
    motto: "断界归灯，极限封顶",
    glyph: "✧",
    artRef: "item:19730",
    unlock: "抵达断界门终试",
    prize: "断界铭牌 / 界裂余债",
    earned: (result) => result.ending.id === "28016" || result.eventIds.includes("20783") || result.itemIds.includes("19730"),
    scarred: (result) => result.eventIds.includes("20784") || result.itemIds.includes("19731"),
  },
];

type BadgeTier = (typeof BADGE_TIERS)[number];
type BadgeOutcome = "clear" | "scar" | "attempt";
type BadgeStatus = "clear" | "scarred" | "attempted" | "unattempted" | "locked";

interface BadgeRecord {
  tier: BadgeTier;
  attempts: RunResultView[];
  clears: RunResultView[];
  scars: RunResultView[];
  bestClear: RunResultView | null;
  bestScar: RunResultView | null;
  latestAttempt: RunResultView | null;
  firstClear: RunResultView | null;
  latestClear: RunResultView | null;
  firstScar: RunResultView | null;
  latestScar: RunResultView | null;
}

function tierOutcome(result: RunResultView, tier: BadgeTier): BadgeOutcome | null {
  if (result.difficulty.level !== tier.level) return null;
  if (tier.earned(result)) return "clear";
  if (tier.scarred?.(result)) return "scar";
  return "attempt";
}

function badgeRecord(results: RunResultView[], tier: BadgeTier): BadgeRecord {
  const attempts = results
    .filter((result) => result.difficulty.level === tier.level)
    .sort((a, b) => b.endedAt - a.endedAt);
  const clears = attempts
    .filter((result) => tierOutcome(result, tier) === "clear")
    .sort((a, b) => b.score - a.score || b.endedAt - a.endedAt);
  const scars = attempts
    .filter((result) => tierOutcome(result, tier) === "scar")
    .sort((a, b) => b.score - a.score || b.endedAt - a.endedAt);
  const byDate = [...clears].sort((a, b) => a.endedAt - b.endedAt);
  const scarsByDate = [...scars].sort((a, b) => a.endedAt - b.endedAt);
  return {
    tier,
    attempts,
    clears,
    scars,
    bestClear: clears[0] ?? null,
    bestScar: scars[0] ?? null,
    latestAttempt: attempts[0] ?? null,
    firstClear: byDate[0] ?? null,
    latestClear: byDate[byDate.length - 1] ?? null,
    firstScar: scarsByDate[0] ?? null,
    latestScar: scarsByDate[scarsByDate.length - 1] ?? null,
  };
}

function clearBadge(result: RunResultView): string | null {
  return BADGE_TIERS.find((tier) => tierOutcome(result, tier) === "clear")?.title ?? null;
}

function badgeStatus(record: BadgeRecord, previousOpen: boolean): BadgeStatus {
  if (record.bestClear) return "clear";
  if (record.bestScar) return "scarred";
  if (record.attempts.length > 0) return "attempted";
  return previousOpen ? "unattempted" : "locked";
}

function statusLine(status: BadgeStatus, record: BadgeRecord): string {
  if (status === "clear") return `${record.bestClear!.score} 分`;
  if (status === "scarred") return `留痕 ${record.bestScar!.score} 分`;
  if (status === "attempted") return `${record.attempts.length} 次尝试`;
  if (status === "unattempted") return "未尝试";
  return "封存";
}

function statusDetail(status: BadgeStatus, record: BadgeRecord): string {
  if (status === "clear") return record.bestClear!.ending.name;
  if (status === "scarred") return record.bestScar!.ending.name;
  if (status === "attempted") return "尚未触发专属铭刻";
  if (status === "unattempted") return record.tier.motto;
  return "先留下前序难度记录";
}

function BadgeWall({ results }: { results: RunResultView[] }) {
  const records = BADGE_TIERS.map((tier) => badgeRecord(results, tier));
  let previousOpen = true;
  const displayRecords = records.map((record) => {
    const status = badgeStatus(record, previousOpen);
    previousOpen = status === "clear" || status === "scarred" || status === "attempted";
    return { ...record, status };
  });
  const clearRecords = displayRecords.filter((record) => record.bestClear);
  const scarRecords = displayRecords.filter((record) => !record.bestClear && record.bestScar);
  const pinnacle = displayRecords
    .map((record) => record.bestClear ?? record.bestScar)
    .filter((result): result is RunResultView => !!result)
    .sort((a, b) => b.difficulty.level - a.difficulty.level || b.score - a.score)[0] ?? null;
  return (
    <div className="badge-wall">
      <div className="badge-wall-head">
        <div>
          <span className="section-kicker">极限铭刻</span>
          <b>{pinnacle ? `最高 D${pinnacle.difficulty.level} · ${pinnacle.difficulty.label}` : "尚未铭刻"}</b>
        </div>
        <span>
          {clearRecords.length}/{BADGE_TIERS.length} 清 · {scarRecords.reduce((sum, record) => sum + record.scars.length, 0)} 伤
        </span>
      </div>
      <div className="badge-grid">
        {displayRecords.map((record) => {
          const { tier, clears, scars, bestClear, bestScar, latestAttempt, firstClear, latestClear, firstScar, latestScar, status } = record;
          const primary = bestClear ?? bestScar ?? latestAttempt;
          const feats = primary ? challengeFeats(primary).slice(0, 3) : [];
          const badgeArt = localArtUrl(tier.artRef);
          return (
            <div
              key={tier.level}
              className={`badge-tile d-${tier.level} ${status}`}
            >
              <span className="badge-glyph" aria-hidden="true">
                {badgeArt ? <img src={badgeArt} alt="" /> : tier.glyph}
              </span>
              <div>
                <b>{tier.title}</b>
                <span>D{tier.level} · {statusLine(status, record)}</span>
                <small>{statusDetail(status, record)}</small>
                <div className="badge-meta" aria-label={`${tier.title} 铭刻状态`}>
                  {status === "clear" ? (
                    <>
                      <i>首刻 {formatDate(firstClear!.endedAt)}</i>
                      <i>{clears.length} 次</i>
                      {scars.length > 0 && <i>{scars.length} 道伤痕</i>}
                      <i>最近 {formatDate(latestClear!.endedAt)}</i>
                    </>
                  ) : status === "scarred" ? (
                    <>
                      <i>首伤 {formatDate(firstScar!.endedAt)}</i>
                      <i>{scars.length} 道伤痕</i>
                      <i>最近 {formatDate(latestScar!.endedAt)}</i>
                      <i>{tier.prize}</i>
                    </>
                  ) : status === "attempted" ? (
                    <>
                      <i>{record.attempts.length} 次尝试</i>
                      <i>最近 {formatDate(latestAttempt!.endedAt)}</i>
                      <i>{tier.unlock}</i>
                    </>
                  ) : status === "unattempted" ? (
                    <>
                      <i>{tier.unlock}</i>
                      <i>{tier.prize}</i>
                    </>
                  ) : (
                    <>
                      <i>前序铭刻未记录</i>
                      <i>{tier.unlock}</i>
                    </>
                  )}
                </div>
                {feats.length > 0 && (
                  <div className="badge-feats">
                    {feats.map((feat) => (
                      <i key={`${tier.level}-${feat.tone}-${feat.label}`} className={`feat-chip mini ${feat.tone}`}>
                        {feat.short}
                      </i>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResultSummary({ result }: { result: RunResultView }) {
  const badge = clearBadge(result);
  const feats = challengeFeats(result);

  return (
    <div
      className={`result-summary d-${result.difficulty.level} ${result.difficulty.level >= 8 ? "pinnacle" : ""}`}
      data-pinnacle={result.difficulty.level >= 8 ? `D${result.difficulty.level} / ${result.difficulty.label}` : undefined}
    >
      <div>
        <span className="section-kicker">最终结算</span>
        {badge && (
          <span className={`clear-badge d-${result.difficulty.level}`}>◆ {badge}</span>
        )}
        <h2>{result.ending.name}</h2>
      </div>
      <div className="score">{result.score} 分</div>
      <div className="score-grid">
        <span>事件 {result.scoreBreakdown.events}</span>
        <span>藏品 {result.scoreBreakdown.items}</span>
        <span>结局 {result.scoreBreakdown.ending}</span>
        <span>声望回响 {scoreEcho(result.scoreBreakdown.multiplier)}</span>
      </div>
      {feats.length > 0 && (
        <div className="feat-list" aria-label="挑战战绩">
          {feats.map((feat) => (
            <span key={`${feat.tone}-${feat.label}`} className={`feat-chip ${feat.tone}`}>
              ◆ {feat.label}
            </span>
          ))}
        </div>
      )}
      <p>{result.ending.des}</p>
    </div>
  );
}

function GeneratingDice() {
  return (
    <div className="generating-dice" aria-hidden="true">
      <Die value={1} phase="rolling" index={0} />
      <Die value={4} phase="rolling" index={1} />
      <Die value={6} phase="rolling" variant="luck" index={2} />
    </div>
  );
}

function StartScreen() {
  const {
    start,
    resume,
    busy,
    error,
    user,
    results,
    historyBusy,
    difficulty,
    setDifficulty,
    setUserName,
    loadResults,
  } = useGame();
  const [mode, setMode] = useState<RunMode>("classic");
  const [theme, setTheme] = useState("");
  const [name, setName] = useState(user?.name ?? "");
  const hasSave = !!localStorage.getItem("roguelife.runId");
  const selectedDifficulty = runDifficultySettings(difficulty);

  useEffect(() => {
    setName(user?.name ?? "");
  }, [user?.name]);

  return (
    <div className={`start start-${mode}`}>
      <div className="ambient" aria-hidden="true">
        <span className="spark s1" />
        <span className="spark s2" />
        <span className="spark s3" />
        <span className="rift" />
      </div>
      <div className="start-orbit" aria-hidden="true">
        <span>⚀</span>
        <span>✦</span>
        <span>⚅</span>
      </div>
      <div className="title-lockup">
        <span className="pixel-mark" aria-hidden="true">RL</span>
        <h1>RogueLife</h1>
        <p className="tagline">骰声落下，裂隙开门 · Dice-bound rift adventure</p>
      </div>

      <div className="start-config">
        <div className="profile-row">
          <input
            className="theme-input"
            value={name}
            placeholder="玩家名"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void setUserName(name)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void setUserName(name);
            }}
          />
        </div>

        <div className="mode-select" role="group" aria-label="选择游玩模式">
          <button
            className={`mode ${mode === "classic" ? "on" : ""}`}
            onClick={() => setMode("classic")}
            aria-pressed={mode === "classic"}
          >
            <span className="mode-icon" aria-hidden="true">I</span>
            <b>经典模式</b>
            <span>沿着旧卷宗踏入固定命运线</span>
          </button>
          <button
            className={`mode ${mode === "infinite" ? "on" : ""}`}
            onClick={() => setMode("infinite")}
            aria-pressed={mode === "infinite"}
          >
            <span className="mode-icon" aria-hidden="true">∞</span>
            <b>无限模式</b>
            <span>让迷雾续写新的事件 / 藏品 / 剧情</span>
          </button>
        </div>

        {mode === "infinite" && (
          <input
            className="theme-input"
            placeholder="可选：输入一个主题（如「赛博朋克侦探」）"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          />
        )}

        <div className="difficulty-select" role="group" aria-label="选择难度">
          {RUN_DIFFICULTY_LEVELS.map((level) => {
            const info = runDifficultySettings(level);
            const on = difficulty === level;
            return (
              <button
                key={level}
                className={`difficulty d-${level} ${on ? "on" : ""}`}
                onClick={() => setDifficulty(level as RunDifficultyLevel)}
                aria-pressed={on}
              >
                <b>D{level}</b>
                <span>{info.label}</span>
                <small>{scoreEcho(info.scoreMultiplier)}</small>
              </button>
            );
          })}
        </div>
        <div className="difficulty-note">
          <b>当前难度 D{selectedDifficulty.level} · {selectedDifficulty.label}</b>
          <div className="difficulty-pressure">
            <span>命运震荡 {pct(selectedDifficulty.minCheckProbability)}-{pct(selectedDifficulty.maxCheckProbability)}</span>
            <span>声望回响 {scoreEcho(selectedDifficulty.scoreMultiplier)}</span>
          </div>
          <div className="rule-list">
            {selectedDifficulty.rules.map((rule) => (
              <span key={rule}>{rule}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="start-actions">
        <button
          className="btn primary big"
          disabled={busy}
          onClick={() =>
            start({
              mode,
              lang: "zh",
              themePrompt: theme || undefined,
              difficulty,
            })
          }
        >
          {busy ? "命运书写中…" : "开始冒险"}
        </button>
        {hasSave && (
          <button className="btn" disabled={busy} onClick={() => resume()}>
            继续上次存档
          </button>
        )}
      </div>
      <BadgeWall results={results} />
      <div className="history">
        <div className="history-head">
          <span className="section-kicker">过往旅程</span>
          <button className="btn small" disabled={historyBusy} onClick={() => void loadResults()}>
            {historyBusy ? "读取中" : "刷新"}
          </button>
        </div>
        {results.length === 0 ? (
          <div className="empty">暂无游玩记录</div>
        ) : (
          <div className="history-list">
            {results.slice(0, 6).map((r) => (
              <div key={r.runId} className="history-row">
                <div>
                  <b>{r.ending.name}</b>
                  <span>
                    {clearBadge(r) && (
                      <i className={`clear-badge mini d-${r.difficulty.level}`}>
                        ◆ {clearBadge(r)}
                      </i>
                    )}
                    D{r.difficulty.level} · {r.difficulty.label}
                  </span>
                  {challengeFeats(r).length > 0 && (
                    <span className="history-feats">
                      {challengeFeats(r).slice(0, 3).map((feat) => (
                        <i key={`${feat.tone}-${feat.label}`} className={`feat-chip mini ${feat.tone}`}>
                          {feat.short}
                        </i>
                      ))}
                    </span>
                  )}
                </div>
                <strong>{r.score} 分</strong>
                <small>
                  {r.eventsCount} 事 / {r.itemsCount} 藏 · {formatDate(r.endedAt)}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default function App() {
  const { started, view, error, generating, reset, initUser } = useGame();
  // Keep returning players on the threshold so they can choose to continue.
  useEffect(() => {
    void initUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!started || !view) return <StartScreen />;

  const ended = view.status === "ended";

  return (
    <div className="app">
      <div className="game-ambient" aria-hidden="true">
        <span className="ember e1" />
        <span className="ember e2" />
        <span className="ember e3" />
        <span className="scanline" />
      </div>

      <header className="topbar">
        <span className="logo">RogueLife</span>
        <div className="topbar-meta">
          <span className="mode-tag">{view.mode === "infinite" ? "无限模式" : "经典模式"}</span>
          <span className="mode-tag">D{view.difficulty.level} · {view.difficulty.label}</span>
        </div>
        <button className="btn small" onClick={() => reset()}>
          新游戏
        </button>
      </header>

      <main className="layout">
        <section className="center">
          <div className="run-status">
            <div className="run-event">
              <span className="section-kicker">当前事件</span>
              <b>{view.event.name}</b>
            </div>
            <div className="run-status-metrics">
              <span>行动 {view.actions.length}</span>
              <span>藏品 {view.player.items.length}</span>
              <span>气运 {view.player.luck ?? 0}</span>
            </div>
          </div>
          <SceneStage key={view.event.id} />
          <NarrativeLog />
          {!ended ? (
            <div className="interaction">
              <ActionList />
              <CheckPanel />
            </div>
          ) : (
            <div className="ended">
              {view.result && <ResultSummary result={view.result} />}
              <button className="btn primary" onClick={() => reset()}>
                开始新的旅程
              </button>
            </div>
          )}
        </section>
        <SidePanel />
      </main>

      {generating && (
        <div className="generating">
          <div className="summon-ring" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <GeneratingDice />
          命运正在翻开下一页…
        </div>
      )}
      {error && <div className="error toast">{error}</div>}
    </div>
  );
}
